import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  HardHat, Cpu, ScanLine, ShieldCheck, Calculator, BarChart3, ArrowRight, CheckCircle2,
  PlayCircle, FolderPlus, FileSpreadsheet, Ruler, ClipboardCheck, Send,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnRzJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MDcxNTcxNXww&ixlib=rb-4.1.0&q=85";

// ── Paste your walkthrough video URL here (YouTube link or a direct .mp4). Leave "" to show the placeholder. ──
const TUTORIAL_VIDEO_URL = "";
const TUTORIAL_POSTER = "https://images.pexels.com/photos/6474476/pexels-photo-6474476.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const toEmbed = (url) => {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  return yt ? `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0` : null;
};

const STEPS = [
  { icon: FolderPlus, title: "1 · Create a project", desc: "Spin up a project and let AI draft a CSI-classified BOQ from a plain-text scope — or a single room photo." },
  { icon: Ruler, title: "2 · Take off quantities", desc: "Measure on an uploaded floor plan with pins, lines and areas; waste presets and assemblies fill the estimate fast." },
  { icon: FileSpreadsheet, title: "3 · Price & refine", desc: "Auto-price from your catalog, add crew hours, and watch labor, material and equipment roll up live." },
  { icon: ClipboardCheck, title: "4 · AI review & harden", desc: "Score competitiveness, benchmark US + local rates, and one-click harden scope-gap and change-order risk." },
  { icon: Send, title: "5 · Send the proposal", desc: "Generate a branded PDF — full breakdown or clean summary — with a secure link clients can accept online." },
];

const FEATURES = [
  { icon: Cpu, title: "AI BOQ Generator", desc: "Turn a plain-text scope into a priced, CSI-classified Bill of Quantities in seconds." },
  { icon: ScanLine, title: "Room Remodel Vision", desc: "Snap a room photo → AI checklist, an 'after' render, and a one-tap priced estimate." },
  { icon: ShieldCheck, title: "Validation Engine", desc: "15 automated checks score every estimate 0–100 before it reaches the client." },
  { icon: Calculator, title: "Cost Catalog", desc: "Benchmarked US unit rates auto-price your line items with fuzzy matching." },
  { icon: BarChart3, title: "Portfolio Analytics", desc: "Cost-by-division, status mix, and S-curve views across every active bid." },
  { icon: HardHat, title: "Project Hub", desc: "Track BOQ totals, risk exposure and quality health from one command center." },
];

export default function Landing() {
  const { user } = useAuth();
  const [playing, setPlaying] = useState(false);
  const embed = TUTORIAL_VIDEO_URL ? toEmbed(TUTORIAL_VIDEO_URL) : null;

  return (
    <div className="min-h-screen bg-background text-foreground dark">
      <header className="h-16 flex items-center justify-between px-5 sm:px-8 border-b border-border sticky top-0 z-40 backdrop-blur-xl bg-background/80">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-primary grid place-items-center"><HardHat className="h-5 w-5 text-primary-foreground" /></div>
          <div className="font-extrabold tracking-tight text-lg">SmartBuild <span className="text-primary">AI</span></div>
        </div>
        <div className="flex items-center gap-3">
          <a href="#demo" data-testid="nav-demo-link" className="hidden sm:inline text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">Watch demo</a>
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard-link" className="text-sm font-bold rounded-lg bg-primary text-primary-foreground px-4 py-2 hover:bg-orange-600 transition-colors inline-flex items-center gap-1.5">
              Go to Dashboard <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">Sign in</Link>
              <Link to="/register" data-testid="nav-signup-link" className="text-sm font-bold rounded-lg bg-primary text-primary-foreground px-4 py-2 hover:bg-orange-600 transition-colors">Start free</Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-[0.25]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-mono uppercase tracking-widest text-primary mb-6">
              <Cpu className="h-3.5 w-3.5" /> AI Cost Estimating
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              From scope to a priced <span className="text-primary">Bill of Quantities</span> in seconds.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              SmartBuild turns a scope description or a single room photo into a CSI-classified, benchmark-priced estimate — then runs the whole bid lifecycle from one hub.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={user ? "/dashboard" : "/register"} data-testid="hero-cta-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-6 py-3 font-bold hover:bg-orange-600 active:bg-orange-700 transition-colors">
                {user ? "Go to your dashboard" : "Build your first estimate"} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#demo" className="inline-flex items-center gap-2 rounded-lg border border-border px-6 py-3 font-semibold hover:bg-secondary transition-colors"><PlayCircle className="h-4 w-4" /> Watch demo</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["CSI MasterFormat", "GPT-5.6 powered", "Bilingual EN / AR"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" />{t}</span>
              ))}
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="rounded-2xl overflow-hidden border border-border shadow-2xl shadow-black/40">
              <img src={HERO} alt="Architect working on blueprints" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-xl border border-border bg-popover px-5 py-4 shadow-xl">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Grand Total</div>
              <div className="text-2xl font-mono font-bold text-primary tabular-nums">$1,248,500.00</div>
            </div>
          </div>
        </div>
      </section>

      {/* Tutorial video */}
      <section id="demo" className="scroll-mt-20 max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-mono uppercase tracking-widest text-primary mb-4">
            <PlayCircle className="h-3.5 w-3.5" /> Full walkthrough
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">See SmartBuild in action</h2>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">A guided tour of every feature — from a photo or scope to a client-ready, priced proposal.</p>
        </div>

        <div className="relative w-full rounded-2xl overflow-hidden border border-border shadow-2xl shadow-black/40 bg-black" style={{ aspectRatio: "16 / 9" }} data-testid="tutorial-video">
          {playing && embed ? (
            <iframe title="SmartBuild tutorial" src={embed} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          ) : playing && TUTORIAL_VIDEO_URL ? (
            <video src={TUTORIAL_VIDEO_URL} controls autoPlay className="absolute inset-0 w-full h-full object-contain bg-black" />
          ) : (
            <button data-testid="video-play-btn" onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full">
              <img src={TUTORIAL_POSTER} alt="SmartBuild walkthrough" className="absolute inset-0 w-full h-full object-cover opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-0 grid place-items-center">
                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
                  <PlayCircle className="h-9 w-9 sm:h-11 sm:w-11" />
                </div>
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-left">
                <div className="text-white font-bold text-base sm:text-lg">Product tour · 4 min</div>
                <div className="text-white/70 text-xs sm:text-sm">Projects → BOQ → Takeoff → AI Review → Proposal</div>
              </div>
            </button>
          )}
        </div>
        {!TUTORIAL_VIDEO_URL && playing && (
          <p className="mt-4 text-center text-sm text-muted-foreground" data-testid="video-placeholder-note">
            Tutorial video coming soon. Add your walkthrough by setting <code className="text-primary">TUTORIAL_VIDEO_URL</code> in <code>Landing.jsx</code>.
          </p>
        )}
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">How it works</h2>
        <p className="text-muted-foreground mb-10">Five steps from first click to a signed proposal.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map((s) => (
            <div key={s.title} className="rounded-xl border border-border bg-card p-5" data-testid={`step-${s.title.charAt(0)}`}>
              <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary grid place-items-center mb-3"><s.icon className="h-5 w-5" /></div>
              <h3 className="font-semibold text-sm mb-1.5">{s.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">One platform, the whole estimate</h2>
        <p className="text-muted-foreground mb-10">Everything an estimator needs, engineered for speed and defensible numbers.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-xl border border-border bg-card p-6 hover:border-primary/50 transition-colors">
              <div className="h-11 w-11 rounded-lg bg-primary/15 text-primary grid place-items-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-lg mb-1.5">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        © 2026 SmartBuild AI Estimator — Charcoal & Safety Orange.
      </footer>
    </div>
  );
}
